/**
 * API Client for Portal (EOIR Data Custodian)
 * Uses Keycloak OIDC Bearer tokens for authentication (ADR-0009).
 */
import { getConfig } from '../config';
import { getUserManager } from '../auth/oidcConfig';

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  errors?: Record<string, string[]>;
}

// REQ-PORTAL-0001-4e184
export class PortalUserNotLinkedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PortalUserNotLinkedError';
  }
}

export class ApiClient {
  private customBaseUrl?: string;

  constructor(baseUrl?: string) {
    this.customBaseUrl = baseUrl;
  }

  private getBaseUrl(): string {
    if (this.customBaseUrl) {
      return this.customBaseUrl;
    }
    return getConfig().apiUrl;
  }

  private buildUrl(endpoint: string): URL {
    const origin =
      typeof window !== 'undefined' && window.location?.origin && window.location.origin !== 'null'
        ? window.location.origin
        : 'http://localhost';
    return new URL(`${this.getBaseUrl()}${endpoint}`, origin);
  }

  /**
   * Get headers with Keycloak OIDC Bearer token.
   */
  private async getHeaders(): Promise<HeadersInit> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    try {
      const mgr = getUserManager();
      const user = await mgr.getUser();
      if (user?.access_token) {
        headers['Authorization'] = `Bearer ${user.access_token}`;
      }
    } catch {
      // No OIDC session — request proceeds without auth header
    }

    return headers;
  }

  /**
   * Handle API response
   */
  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem('portalCustodianName');
        sessionStorage.removeItem('portalSession');
        const mgr = getUserManager();
        await mgr.removeUser();
        window.location.replace('/login');
        throw new Error('Unauthorized');
      }

      if (response.status === 403) {
        let errorData: { errorCode?: string; message?: string } = {};
        try {
          errorData = await response.clone().json();
        } catch {}
        if (errorData.errorCode === 'PORTAL_USER_NOT_LINKED') {
          throw new PortalUserNotLinkedError(
            errorData.message || 'No portal account linked to this identity.'
          );
        }
      }

      let errorMessage = 'An error occurred';
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.Message || errorData.title || errorMessage;
      } catch {
        errorMessage = response.statusText || errorMessage;
      }

      throw new Error(errorMessage);
    }

    if (response.status === 204) {
      return {} as T;
    }

    return response.json();
  }

  async get<T>(endpoint: string, params?: Record<string, string>): Promise<T> {
    const url = this.buildUrl(endpoint);

    if (params) {
      Object.keys(params).forEach((key) => url.searchParams.append(key, params[key]));
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: await this.getHeaders(),
    });

    return this.handleResponse<T>(response);
  }

  async getText(endpoint: string): Promise<string> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.getBaseUrl()}${endpoint}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem('portalCustodianName');
        sessionStorage.removeItem('portalSession');
        const mgr = getUserManager();
        await mgr.removeUser();
        window.location.replace('/login');
        throw new Error('Unauthorized');
      }
      throw new Error(response.statusText || 'Failed to fetch text content');
    }

    return response.text();
  }

  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    const response = await fetch(`${this.getBaseUrl()}${endpoint}`, {
      method: 'POST',
      headers: await this.getHeaders(),
      body: data ? JSON.stringify(data) : undefined,
    });

    return this.handleResponse<T>(response);
  }

  async put<T>(endpoint: string, data: unknown): Promise<T> {
    const response = await fetch(`${this.getBaseUrl()}${endpoint}`, {
      method: 'PUT',
      headers: await this.getHeaders(),
      body: JSON.stringify(data),
    });

    return this.handleResponse<T>(response);
  }

  async patch<T>(endpoint: string, data: unknown): Promise<T> {
    const response = await fetch(`${this.getBaseUrl()}${endpoint}`, {
      method: 'PATCH',
      headers: await this.getHeaders(),
      body: JSON.stringify(data),
    });

    return this.handleResponse<T>(response);
  }

  async delete<T>(endpoint: string): Promise<T> {
    const response = await fetch(`${this.getBaseUrl()}${endpoint}`, {
      method: 'DELETE',
      headers: await this.getHeaders(),
    });

    return this.handleResponse<T>(response);
  }

  async uploadFile<T>(endpoint: string, formData: FormData): Promise<T> {
    const headers: HeadersInit = {};
    try {
      const mgr = getUserManager();
      const user = await mgr.getUser();
      if (user?.access_token) {
        headers['Authorization'] = `Bearer ${user.access_token}`;
      }
    } catch {
      // No OIDC session
    }

    const response = await fetch(`${this.getBaseUrl()}${endpoint}`, {
      method: 'POST',
      headers,
      body: formData,
    });

    return this.handleResponse<T>(response);
  }

  async downloadFile(endpoint: string, filename: string): Promise<void> {
    const response = await fetch(`${this.getBaseUrl()}${endpoint}`, {
      method: 'GET',
      headers: await this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to download file');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }
}

export const apiClient = new ApiClient();
